/**
 * Lesson slot styling: age-group calendar colours, lesson-type and status overrides.
 * Shared between web (inline styles + structural Tailwind) and mobile (RN color tokens).
 */

import {
  DEFAULT_UNGROUPED_CALENDAR_COLOR,
  LEGACY_BALL_COLOR_TO_HEX,
} from '../constants/ageGroupCalendarColors';

export { DEFAULT_UNGROUPED_CALENDAR_COLOR, LEGACY_BALL_COLOR_TO_HEX };

export type CalendarColorMode = 'light' | 'dark';
export type CalendarColorDensity = 'soft' | 'solid';

export interface LessonCalendarColorTokens {
  background: string;
  border: string;
  text: string;
}

/** Camp weekly slots — teal (distinct from private purple). */
const CAMP_SLOT_CLASSES =
  'border border-teal-300 dark:border-teal-700 border-l-teal-500 bg-teal-50 dark:bg-teal-900/30 text-teal-900 dark:text-teal-100';

/** Solid fill for month-calendar camp chips. */
export const CAMP_CALENDAR_CHIP_CLASSES = 'bg-teal-600 text-white dark:bg-teal-500';

export const PRIVATE_PROGRAM_CALENDAR_CHIP_CLASSES = 'bg-purple-500 text-white';
export const PRIVATE_PACKAGE_CALENDAR_CHIP_CLASSES = 'bg-orange-500 text-white';
export const CANCELLED_CALENDAR_CHIP_CLASSES = 'bg-red-500 text-white';
export const POSTPONED_CALENDAR_CHIP_CLASSES = 'bg-rose-500 text-white';
export const RESCHEDULED_CALENDAR_CHIP_CLASSES = 'bg-indigo-500 text-white';

/** Opacity applied to past schedule chips/slots (keeps category colour). */
export const SCHEDULE_PAST_OPACITY_CLASSES = 'opacity-50';

/** @deprecated Use SCHEDULE_PAST_OPACITY_CLASSES appended to the occurrence's normal classes. */
export const PAST_SCHEDULE_SLOT_CLASSES = SCHEDULE_PAST_OPACITY_CLASSES;

export const POSTPONED_SCHEDULE_SLOT_CLASSES =
  'border border-rose-300 dark:border-rose-700 border-l-4 border-l-rose-500 bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200';

export const REPLACEMENT_SCHEDULE_SLOT_CLASSES =
  'border border-indigo-300 dark:border-indigo-700 border-l-4 border-l-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-200';

const STRUCTURAL_SLOT_CLASSES = 'border border-l-4 hover:shadow-md transition-all z-10';

/** Fixed status/type RN tokens (unchanged). */
export const LESSON_SLOT_RN_COLORS: Record<
  'camp' | 'private' | 'privateStandalone' | 'cancelled' | 'postponed' | 'rescheduled' | 'past',
  { background: string; border: string; text: string }
> = {
  camp: { background: '#ccfbf1', border: '#0d9488', text: '#134e4a' },
  private: { background: '#f3e8ff', border: '#a855f7', text: '#581c87' },
  privateStandalone: { background: '#ffedd5', border: '#f97316', text: '#7c2d12' },
  cancelled: { background: '#fee2e2', border: '#ef4444', text: '#991b1b' },
  postponed: { background: '#ffe4e6', border: '#f43f5e', text: '#9f1239' },
  rescheduled: { background: '#e0e7ff', border: '#6366f1', text: '#312e81' },
  past: { background: '#e5e7eb', border: '#9ca3af', text: '#374151' },
};

export type AgeGroupCalendarColorLookup = {
  calendarColor: string;
  label?: string;
  method?: string;
};

export type LessonSlotColorSource = {
  ballColor?: string | null;
  ageGroup?: string | null;
  ageGroupId?: string | null;
  lessonType?: 'group' | 'private' | 'camp' | null;
};

export function appendSchedulePastOpacity(classes: string, isPast: boolean): string {
  if (!isPast) return classes;
  return `${classes} ${SCHEDULE_PAST_OPACITY_CLASSES}`;
}

function parseTimeToMinutes(time: string): number | null {
  const m = time.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

export function isScheduleOccurrencePast(
  date: Date,
  endTime?: string | null,
  now: Date = new Date()
): boolean {
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const occDay = new Date(date);
  occDay.setHours(0, 0, 0, 0);
  if (occDay < todayStart) return true;
  if (occDay > todayStart) return false;
  if (!endTime) return false;
  const endMinutes = parseTimeToMinutes(endTime);
  if (endMinutes == null) return false;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return endMinutes <= nowMinutes;
}

function parseHexColor(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.trim().match(/^#([0-9A-Fa-f]{6})$/);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function toHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`;
}

function mixHex(a: string, b: string, weightB: number): string {
  const ca = parseHexColor(a);
  const cb = parseHexColor(b);
  if (!ca || !cb) return a;
  const w = Math.max(0, Math.min(1, weightB));
  return toHex(
    ca.r * (1 - w) + cb.r * w,
    ca.g * (1 - w) + cb.g * w,
    ca.b * (1 - w) + cb.b * w
  );
}

function relativeLuminance(hex: string): number {
  const c = parseHexColor(hex);
  if (!c) return 0;
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b);
}

function contrastTextForBackground(background: string): string {
  return relativeLuminance(background) > 0.55 ? '#0f172a' : '#f8fafc';
}

/**
 * Derive light/dark calendar tokens from an owner base hex.
 * Soft = weekly slot tints; solid = month chip fills.
 */
export function resolveLessonCalendarColorTokens(
  baseHex: string,
  mode: CalendarColorMode,
  density: CalendarColorDensity = 'soft'
): LessonCalendarColorTokens {
  const base = parseHexColor(baseHex) ? baseHex : DEFAULT_UNGROUPED_CALENDAR_COLOR;
  if (mode === 'light') {
    const background =
      density === 'solid' ? base : mixHex('#ffffff', base, density === 'soft' ? 0.22 : 0.45);
    const border = mixHex(base, '#000000', 0.12);
    return {
      background,
      border,
      text: contrastTextForBackground(background),
    };
  }
  const background =
    density === 'solid'
      ? mixHex(base, '#000000', 0.35)
      : mixHex('#0f172a', base, density === 'soft' ? 0.28 : 0.5);
  const border = mixHex(base, '#ffffff', 0.25);
  return {
    background,
    border,
    text: contrastTextForBackground(background),
  };
}

export function buildAgeGroupCalendarColorLookup(
  ageGroups: Array<{
    id: string;
    calendarColor: string;
    label?: string;
    method?: string;
    isActive?: boolean;
  }>
): Record<string, AgeGroupCalendarColorLookup> {
  const map: Record<string, AgeGroupCalendarColorLookup> = {};
  for (const g of ageGroups) {
    if (g.isActive === false) continue;
    map[g.id] = {
      calendarColor: g.calendarColor,
      label: g.label,
      method: g.method,
    };
  }
  return map;
}

/** @deprecated Use buildAgeGroupCalendarColorLookup */
export const buildAgeGroupBallColorLookup = buildAgeGroupCalendarColorLookup;

export type AgeGroupBallColorLookup = AgeGroupCalendarColorLookup;

export function resolveLessonCalendarBaseHex(
  source: LessonSlotColorSource,
  ageGroupById?: Record<string, AgeGroupCalendarColorLookup>
): string {
  if (source.ageGroupId && ageGroupById) {
    const ag = ageGroupById[source.ageGroupId];
    if (ag?.calendarColor) return ag.calendarColor;
  }
  const legacy = source.ballColor?.trim().toLowerCase();
  if (legacy && LEGACY_BALL_COLOR_TO_HEX[legacy]) {
    return LEGACY_BALL_COLOR_TO_HEX[legacy];
  }
  return DEFAULT_UNGROUPED_CALENDAR_COLOR;
}

export interface LessonSlotStyleProps {
  className: string;
  style?: {
    backgroundColor?: string;
    borderColor?: string;
    color?: string;
  };
}

function tokensToInlineStyle(tokens: LessonCalendarColorTokens): LessonSlotStyleProps['style'] {
  return {
    backgroundColor: tokens.background,
    borderColor: tokens.border,
    color: tokens.text,
  };
}

export function getGroupLessonSlotStyleProps(
  source: LessonSlotColorSource,
  options?: {
    ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
    mode?: CalendarColorMode;
    density?: CalendarColorDensity;
    isPast?: boolean;
  }
): LessonSlotStyleProps {
  const baseHex = resolveLessonCalendarBaseHex(source, options?.ageGroupById);
  const tokens = resolveLessonCalendarColorTokens(
    baseHex,
    options?.mode ?? 'light',
    options?.density ?? 'soft'
  );
  let className = STRUCTURAL_SLOT_CLASSES;
  if (options?.isPast) className = appendSchedulePastOpacity(className, true);
  return { className, style: tokensToInlineStyle(tokens) };
}

export function getLessonSlotClasses(
  _ballColor?: string | null,
  _ageGroup?: string | null,
  lessonType?: 'group' | 'private' | 'camp' | null,
  context?: {
    ageGroupId?: string | null;
    ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
    mode?: CalendarColorMode;
  }
): string {
  if (lessonType === 'camp') {
    return `${STRUCTURAL_SLOT_CLASSES} ${CAMP_SLOT_CLASSES}`;
  }
  const props = getGroupLessonSlotStyleProps(
    {
      ballColor: _ballColor,
      ageGroup: _ageGroup,
      ageGroupId: context?.ageGroupId,
      lessonType,
    },
    { ageGroupById: context?.ageGroupById, mode: context?.mode }
  );
  return props.className;
}

export function getLessonSlotStyleProps(
  source: LessonSlotColorSource,
  options?: {
    ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
    mode?: CalendarColorMode;
    density?: CalendarColorDensity;
    isPast?: boolean;
  }
): LessonSlotStyleProps {
  if (source.lessonType === 'camp') {
    return { className: `${STRUCTURAL_SLOT_CLASSES} ${CAMP_SLOT_CLASSES}` };
  }
  if (source.lessonType === 'private') {
    return {
      className: `${STRUCTURAL_SLOT_CLASSES} border-purple-300 dark:border-purple-700 border-l-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100`,
    };
  }
  return getGroupLessonSlotStyleProps(source, options);
}

export function getLessonSlotClassesForLesson(
  lesson: LessonSlotColorSource,
  ageGroupById?: Record<string, AgeGroupCalendarColorLookup>,
  mode?: CalendarColorMode
): string {
  return getLessonSlotClasses(lesson.ballColor, lesson.ageGroup, lesson.lessonType, {
    ageGroupId: lesson.ageGroupId,
    ageGroupById,
    mode,
  });
}

export function getLessonSlotClassesFromBallColor(
  ballColor?: string | null,
  lessonType?: 'group' | 'private' | 'camp' | null
): string {
  return getLessonSlotClasses(ballColor ?? null, ballColor ? 'juniors' : 'adults', lessonType);
}

export function getLessonCalendarChipClasses(
  lesson: LessonSlotColorSource,
  options?: {
    ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
    isPast?: boolean;
    overrideType?: 'cancelled' | 'postponed' | null;
    isRescheduledReplacement?: boolean;
    isStandalonePrivate?: boolean;
    isCamp?: boolean;
    mode?: CalendarColorMode;
  }
): string {
  let classes: string;

  if (options?.overrideType === 'cancelled') {
    classes = CANCELLED_CALENDAR_CHIP_CLASSES;
  } else if (options?.overrideType === 'postponed') {
    classes = POSTPONED_CALENDAR_CHIP_CLASSES;
  } else if (options?.isRescheduledReplacement) {
    classes = RESCHEDULED_CALENDAR_CHIP_CLASSES;
  } else if (options?.isCamp || lesson.lessonType === 'camp') {
    classes = CAMP_CALENDAR_CHIP_CLASSES;
  } else if (lesson.lessonType === 'private') {
    classes = options?.isStandalonePrivate
      ? PRIVATE_PACKAGE_CALENDAR_CHIP_CLASSES
      : PRIVATE_PROGRAM_CALENDAR_CHIP_CLASSES;
  } else {
    // Group lessons use inline styles via getLessonCalendarChipStyleProps
    classes = '';
  }

  return appendSchedulePastOpacity(classes, !!options?.isPast);
}

export function getLessonCalendarChipStyleProps(
  lesson: LessonSlotColorSource,
  options?: {
    ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
    isPast?: boolean;
    overrideType?: 'cancelled' | 'postponed' | null;
    isRescheduledReplacement?: boolean;
    isStandalonePrivate?: boolean;
    isCamp?: boolean;
    mode?: CalendarColorMode;
  }
): LessonSlotStyleProps {
  const className = getLessonCalendarChipClasses(lesson, options);
  const isGroupChip =
    !options?.overrideType &&
    !options?.isRescheduledReplacement &&
    !options?.isCamp &&
    lesson.lessonType !== 'camp' &&
    lesson.lessonType !== 'private';

  if (!isGroupChip) {
    return { className };
  }

  const baseHex = resolveLessonCalendarBaseHex(lesson, options?.ageGroupById);
  const tokens = resolveLessonCalendarColorTokens(
    baseHex,
    options?.mode ?? 'light',
    'solid'
  );
  let finalClass = className;
  if (options?.isPast) {
    finalClass = appendSchedulePastOpacity(finalClass || 'rounded', true);
  }
  return {
    className: finalClass || 'rounded',
    style: tokensToInlineStyle(tokens),
  };
}

export function getLessonSlotRnColors(
  lesson: LessonSlotColorSource,
  options?: {
    ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
    isPast?: boolean;
    overrideType?: 'cancelled' | 'postponed' | null;
    isRescheduledReplacement?: boolean;
    isStandalonePrivate?: boolean;
    mode?: CalendarColorMode;
  }
): { background: string; border: string; text: string } {
  if (options?.overrideType === 'cancelled') return LESSON_SLOT_RN_COLORS.cancelled;
  if (options?.overrideType === 'postponed') return LESSON_SLOT_RN_COLORS.postponed;
  if (options?.isRescheduledReplacement) return LESSON_SLOT_RN_COLORS.rescheduled;
  if (lesson.lessonType === 'camp') return LESSON_SLOT_RN_COLORS.camp;
  if (lesson.lessonType === 'private') {
    return options?.isStandalonePrivate
      ? LESSON_SLOT_RN_COLORS.privateStandalone
      : LESSON_SLOT_RN_COLORS.private;
  }
  const baseHex = resolveLessonCalendarBaseHex(lesson, options?.ageGroupById);
  return resolveLessonCalendarColorTokens(baseHex, options?.mode ?? 'light', 'soft');
}

/** @deprecated Use resolveLessonCalendarBaseHex */
export function resolveLessonSlotBallColor(
  source: LessonSlotColorSource,
  ageGroupById?: Record<string, AgeGroupCalendarColorLookup>
): string {
  return resolveLessonCalendarBaseHex(source, ageGroupById);
}

export function getScheduleOccurrenceSlotClasses(
  lesson: LessonSlotColorSource,
  ageGroupById: Record<string, AgeGroupCalendarColorLookup> | undefined,
  isPast: boolean,
  mode?: CalendarColorMode
): string {
  return appendSchedulePastOpacity(
    getLessonSlotClassesForLesson(lesson, ageGroupById, mode),
    isPast
  );
}

export function getScheduleOccurrenceSlotStyleProps(
  lesson: LessonSlotColorSource,
  ageGroupById: Record<string, AgeGroupCalendarColorLookup> | undefined,
  isPast: boolean,
  mode?: CalendarColorMode
): LessonSlotStyleProps {
  const props = getLessonSlotStyleProps(lesson, { ageGroupById, mode, isPast });
  return props;
}

// Legacy exports for consumers that imported ball colour constants
export const LESSON_SLOT_BALL_COLORS = ['blue', 'red', 'orange', 'green', 'yellow'] as const;
export type LessonSlotBallColor = (typeof LESSON_SLOT_BALL_COLORS)[number];
